import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, IconButton,
  Select, MenuItem, FormControl, InputLabel, Tooltip,
  CircularProgress, Alert, Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Verified as ApproveIcon,
  PictureAsPdf as CoaIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'primary'> = {
  pending: 'warning',
  passed: 'success',
  failed: 'error',
  approved: 'primary',
};

const sampleTypeLabels: Record<string, string> = {
  grain_lot: 'Grain Lot',
  tempering: 'Tempering',
  finished_product: 'Finished Product',
};

const QualityTestList: React.FC = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => { fetchTests(); }, [statusFilter, typeFilter]);

  const fetchTests = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.sampleType = typeFilter;
      const res = await millingApi.getQualityTests(params);
      setTests(res.data || res || []);
    } catch {
      setError('Failed to load quality tests.');
    } finally {
      setLoading(false);
    }
  };

  const handlePass = async (id: string) => {
    try { await millingApi.passQualityTest(id); fetchTests(); } catch { setError('Action failed.'); }
  };
  const handleFail = async (id: string) => {
    try { await millingApi.failQualityTest(id); fetchTests(); } catch { setError('Action failed.'); }
  };
  const handleApprove = async (id: string) => {
    try { await millingApi.approveQualityTest(id); fetchTests(); } catch { setError('Action failed.'); }
  };
  const handleDelete = async (id: string, testNumber: string) => {
    if (!window.confirm(`Delete test ${testNumber}?`)) return;
    try { await millingApi.deleteQualityTest(id); fetchTests(); } catch { setError('Delete failed.'); }
  };

  const fmt = (v: any, unit = '') => (v != null ? `${Number(v).toFixed(2)}${unit}` : '-');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Quality Tests</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/milling/quality-tests/new')}>
          New Test
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="passed">Passed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Sample Type</InputLabel>
              <Select value={typeFilter} label="Sample Type" onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="grain_lot">Grain Lot</MenuItem>
                <MenuItem value="tempering">Tempering</MenuItem>
                <MenuItem value="finished_product">Finished Product</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Card}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Test #</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Moist%</TableCell>
                <TableCell>Protein%</TableCell>
                <TableCell>Ash%</TableCell>
                <TableCell>FN(s)</TableCell>
                <TableCell>WG%</TableCell>
                <TableCell>WA%</TableCell>
                <TableCell>Stab</TableCell>
                <TableCell>Afla(ppb)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={15} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No quality tests found.
                  </TableCell>
                </TableRow>
              ) : (
                tests.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{t.testNumber}</TableCell>
                    <TableCell>
                      <Chip label={sampleTypeLabels[t.sampleType] || t.sampleType} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{t.referenceNumber || '-'}</TableCell>
                    <TableCell>{t.productName || '-'}</TableCell>
                    <TableCell>{t.sampleDate}</TableCell>
                    <TableCell>{fmt(t.moisture, '%')}</TableCell>
                    <TableCell>{fmt(t.protein, '%')}</TableCell>
                    <TableCell>{fmt(t.ash, '%')}</TableCell>
                    <TableCell>{t.fallingNumber != null ? Number(t.fallingNumber).toFixed(0) : '-'}</TableCell>
                    <TableCell>{fmt(t.wetGluten, '%')}</TableCell>
                    <TableCell>{fmt(t.waterAbsorption, '%')}</TableCell>
                    <TableCell>{t.stability != null ? `${Number(t.stability).toFixed(1)}min` : '-'}</TableCell>
                    <TableCell>
                      {t.aflatoxin != null ? (
                        <Chip
                          label={Number(t.aflatoxin).toFixed(1)}
                          size="small"
                          color={Number(t.aflatoxin) > 10 ? 'error' : 'success'}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.status}
                        size="small"
                        color={statusColors[t.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {t.status === 'pending' && (
                          <>
                            <Tooltip title="Mark Passed">
                              <IconButton size="small" color="success" onClick={() => handlePass(t.id)}>
                                <PassIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark Failed">
                              <IconButton size="small" color="error" onClick={() => handleFail(t.id)}>
                                <FailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {t.status === 'passed' && (
                          <Tooltip title="Approve">
                            <IconButton size="small" color="primary" onClick={() => handleApprove(t.id)}>
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {t.referenceId && (
                          <Tooltip title="View CoA">
                            <IconButton size="small" onClick={() => navigate(`/milling/quality-tests/coa/${t.referenceId}`)}>
                              <CoaIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => navigate(`/milling/quality-tests/${t.id}/edit`)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(t.id, t.testNumber)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default QualityTestList;
